import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PasswordStrengthIndicatorProps {
  password: string;
  confirmPassword?: string;
  showRequirements?: boolean;
}

const requirements = [
  {
    label: 'Mínimo 8 caracteres',
    test: (p: string) => p.length >= 8
  },
  {
    label: 'Al menos un número',
    test: (p: string) => /\d/.test(p)
  },
  {
    label: 'Al menos un carácter especial (!@#$%^&*)',
    test: (p: string) => /[!@#$%^&*(),.?":{}|<>]/.test(p)
  }
];

const calculateStrength = (password: string): number => {
  if (!password) return 0;
  
  let score = 0;
  
  // Longitud
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  
  // Números
  if (/\d/.test(password)) score += 1;
  
  // Caracteres especiales
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1;
  
  // Mayúsculas y minúsculas
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  
  return score; // 0-5
};

const getStrengthInfo = (score: number): { label: string; color: string; percentage: number } => {
  switch (score) {
    case 0:
    case 1:
      return { label: 'Muy débil', color: 'bg-red-500', percentage: 20 };
    case 2:
      return { label: 'Débil', color: 'bg-orange-500', percentage: 40 };
    case 3:
      return { label: 'Moderada', color: 'bg-yellow-500', percentage: 60 };
    case 4:
      return { label: 'Fuerte', color: 'bg-green-400', percentage: 80 };
    case 5:
      return { label: 'Muy fuerte', color: 'bg-green-600', percentage: 100 };
    default:
      return { label: 'Muy débil', color: 'bg-red-500', percentage: 20 };
  }
};

export function validatePassword(password: string): boolean {
  return requirements.every(req => req.test(password));
}

export function PasswordStrengthIndicator({ 
  password, 
  confirmPassword, 
  showRequirements = true 
}: PasswordStrengthIndicatorProps) {
  const score = calculateStrength(password);
  const { label, color, percentage } = getStrengthInfo(score);
  const passwordsMatch = confirmPassword !== undefined && password === confirmPassword && password.length > 0;
  const showMatchStatus = confirmPassword !== undefined && confirmPassword.length > 0;

  if (!password) return null;

  return (
    <div className="space-y-3">
      {/* Barra de fortaleza */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Fortaleza:</span>
          <span className={cn(
            "font-medium",
            score <= 1 && "text-red-500",
            score === 2 && "text-orange-500",
            score === 3 && "text-yellow-600",
            score >= 4 && "text-green-600"
          )}>
            {label}
          </span>
        </div>
        <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
          <div 
            className={cn("h-full transition-all duration-300", color)}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      {/* Requisitos */}
      {showRequirements && (
        <div className="space-y-1.5">
          {requirements.map((req, index) => {
            const passed = req.test(password);
            return (
              <div 
                key={index} 
                className={cn(
                  "flex items-center gap-2 text-xs transition-colors",
                  passed ? "text-green-600" : "text-muted-foreground"
                )}
              >
                {passed ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <X className="h-3.5 w-3.5" />
                )}
                <span>{req.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Estado de coincidencia */}
      {showMatchStatus && (
        <div className={cn(
          "flex items-center gap-2 text-xs transition-colors",
          passwordsMatch ? "text-green-600" : "text-red-500"
        )}>
          {passwordsMatch ? (
            <Check className="h-3.5 w-3.5" />
          ) : (
            <X className="h-3.5 w-3.5" />
          )}
          <span>{passwordsMatch ? 'Las contraseñas coinciden' : 'Las contraseñas no coinciden'}</span>
        </div>
      )}
    </div>
  );
}
